import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { useTheme } from '@mui/material/styles';
import { INotification } from '../../_api/notification.api';
import { NotificationList } from './NotificationList';

type NotificationFilter = 'all' | 'unread' | 'read';

interface INotificationDialogProps {
    open: boolean;
    onClose: () => void;
    notifications: INotification[];
    onNotificationClick?: (notification: INotification) => void;
    onMarkAllAsRead?: () => void;
}

/**
 * NotificationDialog Component
 * 
 * Displays notifications in a dialog/modal with filtering options.
 * Follows Open/Closed Principle - can be extended without modification.
 */
export const NotificationDialog: React.FC<INotificationDialogProps> = ({
    open,
    onClose,
    notifications,
    onNotificationClick,
    onMarkAllAsRead,
}) => {
    const theme = useTheme();
    const [filter, setFilter] = useState<NotificationFilter>('all');
    
    const unreadCount = notifications.filter(n => !n.isRead).length;

    const handleFilterChange = (
        event: React.MouseEvent<HTMLElement>,
        newFilter: NotificationFilter | null,
    ) => {
        if (newFilter !== null) {
            setFilter(newFilter);
        }
    };

    // Filter and sort notifications
    const filteredNotifications = notifications
        .filter(n => {
            if (filter === 'unread') return !n.isRead;
            if (filter === 'read') return n.isRead;
            return true; // 'all'
        })
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    maxHeight: '80vh',
                }
            }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                pb: 1,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" component="span">
                        Notifications
                    </Typography>
                    {unreadCount > 0 && (
                        <Box
                            sx={{
                                px: 1,
                                py: 0.25,
                                borderRadius: 1,
                                backgroundColor: theme.palette.primary.main,
                                color: theme.palette.primary.contrastText,
                            }}
                        >
                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                {unreadCount} new
                            </Typography>
                        </Box>
                    )}
                </Box>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        color: theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <Box sx={{ px: 3, pb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                    <ToggleButtonGroup
                        value={filter}
                        exclusive
                        onChange={handleFilterChange}
                        size="small"
                        aria-label="notification filter"
                    >
                        <ToggleButton value="all" aria-label="all notifications">
                            All
                        </ToggleButton>
                        <ToggleButton value="unread" aria-label="unread notifications">
                            New
                        </ToggleButton>
                        <ToggleButton value="read" aria-label="read notifications">
                            Viewed
                        </ToggleButton>
                    </ToggleButtonGroup>

                    {unreadCount > 0 && onMarkAllAsRead && (
                        <Button 
                            size="small" 
                            onClick={onMarkAllAsRead}
                            sx={{ textTransform: 'none' }}
                        >
                            Mark all as read
                        </Button>
                    )}
                </Box>
            </Box>

            <DialogContent sx={{ p: 0 }}>
                <NotificationList
                    notifications={filteredNotifications}
                    onNotificationClick={onNotificationClick}
                    emptyMessage={
                        filter === 'unread' 
                            ? 'No new notifications' 
                            : filter === 'read' 
                            ? 'No viewed notifications'
                            : 'No notifications yet'
                    }
                />
            </DialogContent>
        </Dialog>
    );
};
